import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TdoDropdownChange } from '../../tdo-controls/common/tdo-dropdown-v2/models/tdo-dropdown-change';
import { Globals } from '../../globals';
import { VitalSignsService } from '../../services/vital-signs.service';
import { VitalSignsDataModel, VitalSignsDto, SaveVitalSignRequest } from '../../models/vital-signs/vital-signs-data.model';
import { 
  MT_WeightUnit_kg, IM_WeightUnit_lb, 
  MT_LengthUnit_m, IM_LengthUnit_f, IM_LengthUnit_in, MT_LengthUnit_cm,
  MT_TemperatureUnit_Celsius, IM_TemperatureUnit_Fahrenheit 
} from '../../constants';
import { NotesService } from '../../services/notes.service';
import { TdoNoteAssociatedObject, TdoNoteSaveModel } from '../../main-interface/controls/tdo-notes/models/tdo-note-save-model';
import { NoteDataModel } from '../../models/notes/note-data.model';
import { MessagingService } from '../../services/messaging.service';
import { MessagingConstants } from '../../models/MessagingConstants';
import { NoteTypeEnum } from '../../models/enum/note-type.enum';
import { NoteCategoryEnum } from '../../models/enum/note-category.enum';
import { finalize } from 'rxjs/operators';

export interface VitalSignsModel {
  vitalSignsId?: string; // For tracking existing records
  isExisting?: boolean; // Flag to identify existing vs new records
  taken: string; // Display string for UI
  takenDate?: Date; // Actual Date object for accurate parsing
  takenBy: string;
  anesthesiaAdministered: boolean;
  weight: number;
  weightUnitId: string;
  height: number;
  heightUnitId: string;
  height2: number;
  systolic: number;
  diastolic: number;
  temperature: number;
  temperatureUnitId: string;
  oxygenSaturation: number;
  pulse: number;
}

export interface UnitOption {
  value: string;
  display: string;
}

@Component({
  standalone: false,
  selector: 'app-vital-signs-dialog',
  templateUrl: './vital-signs-dialog.component.html',
  styleUrls: ['./vital-signs-dialog.component.scss']
})
export class VitalSignsDialogComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  private isDestroyed = false;
  allEntries: VitalSignsModel[] = [];
  
  // Pulse validation constants
  public readonly MIN_PULSE = 30; // Minimum reasonable pulse (bradycardia threshold)
  public readonly MAX_PULSE = 220; // Maximum reasonable pulse (tachycardia threshold)
  
  // Units options
  unitsOptions: UnitOption[] = [
    { value: 'imperial', display: 'Imperial' },
    { value: 'metric', display: 'Metric' }
  ];
  
  selectedUnits: UnitOption;

  constructor(
    public dialogRef: MatDialogRef<VitalSignsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly snackBar: MatSnackBar,
    public readonly globals: Globals,
    private readonly vitalSignsService: VitalSignsService,
    private readonly notesService: NotesService,
    private readonly messagingService: MessagingService,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Initialize with default units first, will be updated in ngOnInit
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  ngOnInit(): void {
    this.loadSavedUnits();
    this.loadExistingEntries(); // This will handle creating empty row if no data
  }



  private loadSavedUnits(): void {
    // Load saved unit preference from localStorage
    const savedUnitID = localStorage.getItem('vitalUnitID');
    
    if (savedUnitID) {
      // Find the unit option that matches the saved display value
      const savedUnit = this.unitsOptions.find(unit => unit.display === savedUnitID);
      this.selectedUnits = savedUnit || this.unitsOptions[0]; // Default to Imperial if not found
    } else {
      // Default to Imperial if no saved preference
      this.selectedUnits = this.unitsOptions[0];
    }
  }

  private saveUnitsPreference(unitDisplay: string): void {
    // Save the full display label to localStorage
    localStorage.setItem('vitalUnitID', unitDisplay);
  }

  private loadExistingEntries(): void {
    const patientId = this.data?.patientId || this.globals.currentPatientId;

    if (!patientId) {
      console.warn('No patient ID available for loading vital signs');
      this.allEntries = [];
      return;
    }

    this.isLoading = true;

    this.vitalSignsService.getPatientVitalSigns(patientId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          if (!this.isDestroyed) {
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe({
        next: (vitalSignsData: VitalSignsDto[]) => {
          this.allEntries = vitalSignsData?.length > 0
            ? this.convertBackendDataToModel(vitalSignsData)
            : [];
        },
        error: (error) => {
          console.error('Error loading vital signs:', error);
          this.createMessage('Error loading vital signs data', 'Error');
          this.allEntries = [];
        }
      });
  }

  private findTodaysRecordWithMatchingUnits(isMetric: boolean): { weight: number, height: number, height2: number } | null {
    const today = new Date();
    const todayDateString = today.toDateString(); // e.g., "Thu Oct 10 2025"
    
    // Expected unit IDs based on current selection
    const expectedWeightUnitId = isMetric ? MT_WeightUnit_kg : IM_WeightUnit_lb;
    const expectedHeightUnitId = isMetric ? MT_LengthUnit_m : IM_LengthUnit_f;
    
    // Find existing records from today with matching units
    const todaysRecords = this.allEntries.filter(entry => {
      if (!entry.isExisting || !entry.takenDate) return false;
      
      // Check if the record is from today
      const entryDateString = entry.takenDate.toDateString();
      if (entryDateString !== todayDateString) return false;
      
      // Check if units match current selection
      const weightUnitsMatch = entry.weightUnitId === expectedWeightUnitId;
      const heightUnitsMatch = entry.heightUnitId === expectedHeightUnitId;
      
      // Only consider records where both weight and height have matching units
      return weightUnitsMatch && heightUnitsMatch;
    });
    
    // Find the most recent record with both weight and height values
    const recordWithBothValues = todaysRecords.find(record => 
      record.weight !== null && record.weight !== undefined &&
      record.height !== null && record.height !== undefined
    );
    
    if (recordWithBothValues) {
      return {
        weight: recordWithBothValues.weight,
        height: recordWithBothValues.height,
        height2: recordWithBothValues.height2
      };
    }
    
    return null;
  }

  private createEmptyEntry(): VitalSignsModel {
    const isMetric = this.selectedUnits?.value === 'metric';
    
    // Auto-populate current date/time and user
    const now = new Date();
    const currentDateTime = this.formatDateTime(now);
    const currentUser = this.getCurrentUserDisplayName();
    
    // Try to auto-populate height and weight from today's existing records
    const todaysRecord = this.findTodaysRecordWithMatchingUnits(isMetric);
    
    return {
      isExisting: false, // Mark as new record
      taken: currentDateTime,
      takenDate: now, // Store actual Date object for accurate parsing
      takenBy: currentUser,
      anesthesiaAdministered: false,
      weight: todaysRecord?.weight || null,
      weightUnitId: isMetric ? MT_WeightUnit_kg : IM_WeightUnit_lb,
      height: todaysRecord?.height || null,
      heightUnitId: isMetric ? MT_LengthUnit_m : IM_LengthUnit_f,
      height2: todaysRecord?.height2 || null,
      systolic: null,
      diastolic: null,
      temperature: null,
      temperatureUnitId: isMetric ? MT_TemperatureUnit_Celsius : IM_TemperatureUnit_Fahrenheit,
      oxygenSaturation: null,
      pulse: null
    };
  }

  private convertBackendDataToModel(backendData: VitalSignsDto[]): VitalSignsModel[] {
    // Sort by VitalSignsMeasuredOn date (most recent first)
    const sortedData = backendData.sort((a, b) => {
      const dateA = a.VitalSignsMeasuredOn ? new Date(a.VitalSignsMeasuredOn).getTime() : 0;
      const dateB = b.VitalSignsMeasuredOn ? new Date(b.VitalSignsMeasuredOn).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    return sortedData.map(data => ({
      vitalSignsId: data.VitalSignsID, // Include the ID for updates/deletes
      isExisting: true, // Mark as existing record
      taken: this.formatDateTime(data.VitalSignsMeasuredOn),
      takenDate: data.VitalSignsMeasuredOn ? this.parseUTCDate(data.VitalSignsMeasuredOn) : undefined,
      takenBy: data.UserFullName || 'Unknown User', // Use UserFullName from backend for existing records
      anesthesiaAdministered: !!(data.AnesthesiaID && data.AnesthesiaID.trim() !== ''),
      weight: data.SubjectWeight || null,
      weightUnitId: data.enumWeightUnitID || '',
      height: data.SubjectHeight || null,
      heightUnitId: data.enumLengthUnitID || '',
      height2: data.SubjectHeight2 || null,
      systolic: data.BloodPressureSystolic || null,
      diastolic: data.BloodPressureDiastolic || null,
      temperature: data.SubjectTemperature || null,
      temperatureUnitId: data.enumTemperatureUnitID || '',
      oxygenSaturation: data.OxygenSaturationPCT || null,
      pulse: data.Pulse || null
    }));
  }

  private parseUTCDate(dateTime: Date | string): Date {
    if (typeof dateTime === 'string') {
      // Backend sends UTC time as string like "2025-10-10 23:34:00.000"
      let dateString = dateTime.trim();
      
      // Force UTC interpretation by adding 'Z' if not present
      if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        // Replace space with 'T' if needed and add 'Z' to force UTC interpretation
        if (dateString.includes(' ')) {
          dateString = dateString.replace(' ', 'T');
        }
        if (!dateString.endsWith('Z')) {
          dateString += 'Z';
        }
      }
      
      return new Date(dateString);
    }
    return dateTime;
  }

  private formatDateTime(dateTime?: Date | string): string {
    if (!dateTime) return '';
    
    // Parse the date properly (handling UTC conversion)
    const date = this.parseUTCDate(dateTime);
    
    // Verify the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date received:', dateTime);
      return '';
    }
    
    // Format in local timezone (this will automatically convert UTC to local)
    const dateStr = date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    return `${dateStr} ${timeStr}`;
  }

  private getUserDisplayName(userId?: string): string {
    // TODO: You might want to implement user lookup here based on userId
    // For now, return the current user from localStorage
    return this.getCurrentUserDisplayName();
  }

  private getCurrentUserDisplayName(): string {
    try {
      const storedUser = JSON.parse(localStorage.getItem('storedUser'));
      if (storedUser) {
        // Try different user name properties in order of preference
        return storedUser.userdisplayname || 
               storedUser.fullname || 
               storedUser.nickname || 
               storedUser.authdata?.userdisplayname ||
               storedUser.authdata?.userfullname ||
               'Current User';
      }
    } catch (error) {
      console.error('Error parsing stored user from localStorage:', error);
    }
    
    // Fallback to globals if localStorage fails
    return this.globals.currentProviderDoctorFullName || 'Unknown User';
  }

  // Predicate functions for dropdowns
  unitsPredicate = (unit: UnitOption): string => {
    return unit.display;
  };

  unitDisplayPredicate = (unit: UnitOption): string => {
    return unit.display;
  };

  // Event handlers
  onUnitsSelectionChange(event: TdoDropdownChange): void {
    const selectedUnit: UnitOption = event.value;
    this.selectedUnits = selectedUnit;
    
    // Save the unit preference to localStorage using the full display label
    this.saveUnitsPreference(selectedUnit.display);
    
    // Update the current model with new unit IDs
    this.updateModelUnits();
    
    // Units are now automatically updated in the template based on selectedUnits
  }

  private updateModelUnits(): void {
    const isMetric = this.selectedUnits?.value === 'metric';
    
    // Update unit IDs only in new entries (not existing records)
    this.allEntries.forEach(entry => {
      if (!entry.isExisting) {
        entry.weightUnitId = isMetric ? MT_WeightUnit_kg : IM_WeightUnit_lb;
        entry.heightUnitId = isMetric ? MT_LengthUnit_m : IM_LengthUnit_f;
        entry.temperatureUnitId = isMetric ? MT_TemperatureUnit_Celsius : IM_TemperatureUnit_Fahrenheit;
      }
    });
  }

  onAddClick(): void {
    // Add a new empty row at the beginning for better visibility
    const newEntry = this.createEmptyEntry();
    this.allEntries.unshift(newEntry);
    
    this.createMessage('New vital signs row added', 'Success');
  }


  private isRecordOlderThan24Hours(entry: VitalSignsModel): boolean {
    if (!entry.takenDate) return false;
    
    const now = new Date();
    const recordDate = entry.takenDate;
    const timeDifferenceMs = now.getTime() - recordDate.getTime();
    const twentyFourHoursMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    
    return timeDifferenceMs > twentyFourHoursMs;
  }

  canDeleteEntry(entry: VitalSignsModel): boolean {
    // New records (not existing) can always be deleted
    if (!entry.isExisting) return true;
    
    // Existing records can only be deleted if they're less than 24 hours old
    return !this.isRecordOlderThan24Hours(entry);
  }

  onDeleteEntry(index: number): void {
    if (index >= 0 && index < this.allEntries.length) {
      const entryToDelete = this.allEntries[index];
      
      // Check if record is older than 24 hours (only for existing records)
      if (entryToDelete.isExisting && this.isRecordOlderThan24Hours(entryToDelete)) {
        this.createMessage('Cannot delete records older than 24 hours', 'Warning');
        return;
      }
      
      // If the entry has a vital signs ID, delete it from the backend
      if (entryToDelete.vitalSignsId) {
        this.isLoading = true;
        
        this.vitalSignsService.deleteVitalSignById(entryToDelete.vitalSignsId).subscribe({
          next: () => {
            this.removeEntryFromList(index);
            this.isLoading = false;
            this.createMessage('Entry deleted successfully', 'Success');
          },
          error: (error) => {
            console.error('Error deleting vital sign from backend:', error);
            this.isLoading = false;
            this.createMessage('Error deleting entry from server', 'Error');
          }
        });
      } else {
        // Just remove from local list (not saved to backend yet)
        this.removeEntryFromList(index);
        this.createMessage('Entry removed', 'Success');
      }
    }
  }

  private removeEntryFromList(index: number): void {
    this.allEntries.splice(index, 1);
    
    // Allow empty list - user can click Add to create new entries when needed
  }

  onCancelClick(): void {
    this.dialogRef.close(null);
  }

  onSaveClick(): void {
    if (this.isLoading) {
      return;
    }

    // Filter to only new entries (not existing records) that have data
    const entriesToSave = this.allEntries.filter(entry => 
      !entry.isExisting && this.hasEntryData(entry)
    );

    if (entriesToSave.length === 0) {
      this.createMessage('No new data to save', 'Warning');
      return;
    }

    // Convert to backend format and save
    const saveRequests = this.convertModelToSaveRequests(entriesToSave);
    const patientId = this.data?.patientId || this.globals.currentPatientId;

    if (!patientId) {
      this.createMessage('Patient ID is required to save vital signs', 'Error');
      return;
    }

    this.isLoading = true;

    // Save each vital sign individually using the single save endpoint
    this.saveVitalSignsSequentially(saveRequests, 0, []);
  }

  private saveVitalSignsSequentially(requests: SaveVitalSignRequest[], index: number, savedRecords: VitalSignsDto[]): void {
    if (index >= requests.length) {
      // All saves completed successfully
      this.createMessage(`${savedRecords.length} vital sign(s) saved successfully`, 'Success');
      
      try {
        // Create note for the saved vital signs
        this.createVitalSignsNote(savedRecords);
      } catch (error) {
        console.error('Error creating vital signs notes:', error);
      }
      
      // Reload data from database instead of closing dialog
      this.reloadVitalSignsData();
      return;
    }

    // Save the current request
    const currentRequest = requests[index];

    this.vitalSignsService.saveVitalSign(currentRequest).subscribe({
      next: (savedRecord: VitalSignsDto) => {
        savedRecords.push(savedRecord);
        
        // Continue with the next request
        this.saveVitalSignsSequentially(requests, index + 1, savedRecords);
      },
      error: (error) => {
        console.error(`Error saving vital sign ${index + 1}:`, error);
        this.isLoading = false;
        this.createMessage(`Error saving vital sign ${index + 1}`, 'Error');
      }
    });
  }

  private reloadVitalSignsData(): void {
    const patientId = this.data?.patientId || this.globals.currentPatientId;

    if (!patientId) {
      console.warn('No patient ID available for reloading vital signs');
      // No patient ID - show empty list
      this.allEntries = [];
      this.isLoading = false;
      if (!this.isDestroyed) {
        this.cdr.detectChanges();
      }
      return;
    }

    this.isLoading = true;

    this.vitalSignsService.getPatientVitalSigns(patientId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          if (!this.isDestroyed) {
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe({
        next: (vitalSignsData: VitalSignsDto[]) => {
          if (vitalSignsData && vitalSignsData.length > 0) {
            // Convert backend data to read-only entries
            this.allEntries = this.convertBackendDataToModel(vitalSignsData);
          } else {
            // No data from backend - show empty list
            this.allEntries = [];
          }
        },
        error: (error) => {
          console.error('Error reloading vital signs:', error);
          this.createMessage('Error reloading vital signs data', 'Error');

          // On error, show empty list
          this.allEntries = [];
        }
      });
  }

  private hasEntryData(entry: VitalSignsModel): boolean {
    return !!(
      entry.taken ||
      entry.takenBy ||
      entry.anesthesiaAdministered ||
      entry.weight !== null ||
      entry.height !== null ||
      entry.systolic !== null ||
      entry.diastolic !== null ||
      entry.temperature !== null ||
      entry.oxygenSaturation !== null ||
      entry.pulse !== null
    );
  }

  isPulseValid(pulse: number): boolean {
    if (pulse === null || pulse === undefined) {
      return true; // Null/undefined is valid (optional field)
    }
    return pulse >= this.MIN_PULSE && pulse <= this.MAX_PULSE;
  }

  getPulseValidationMessage(pulse: number): string {
    if (this.isPulseValid(pulse)) {
      return ''; // Valid pulse, no message needed
    }
    if (pulse < this.MIN_PULSE) {
      return `Pulse too low (minimum: ${this.MIN_PULSE} bpm)`;
    }
    if (pulse > this.MAX_PULSE) {
      return `Pulse too high (maximum: ${this.MAX_PULSE} bpm)`;
    }
    return '';
  }

  private convertModelToSaveRequests(modelData: VitalSignsModel[]): SaveVitalSignRequest[] {
    const patientId = this.data?.patientId || this.globals.currentPatientId;
    
    return modelData.map(model => ({
      VitalSignsID: undefined, // Always undefined for new records (create operation)
      PatientID: patientId,
      VitalReasonDescription: 'Routine Vital Signs', // You might want to make this configurable
      // AnesthesiaID will be handled by the anesthesia component, not this vital signs component
      AnesthesiaID: undefined, // New records don't have anesthesia data from this component
      VitalSignsMeasuredOn: model.takenDate || new Date(),
      BloodPressureSystolic: model.systolic || undefined,
      BloodPressureDiastolic: model.diastolic || undefined,
      SubjectWeight: model.weight || undefined,
      enumWeightUnitID: model.weightUnitId || undefined,
      SubjectHeight: model.height || undefined,
      enumLengthUnitID: model.heightUnitId || undefined,
      SubjectHeight2: model.height2 || undefined,
      SubjectTemperature: model.temperature || undefined,
      enumTemperatureUnitID: model.temperatureUnitId || undefined,
      OxygenSaturationPCT: model.oxygenSaturation || undefined,
      Pulse: model.pulse || undefined
    }));
  }


  private parseDateTime(dateTimeString: string): Date | null {
    if (!dateTimeString) return null;
    
    try {
      // Try to parse the date/time string
      const parsed = new Date(dateTimeString);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }

  private generateAnesthesiaId(): string {
    // TODO: You might want to implement proper anesthesia ID generation
    // For now, return a placeholder GUID
    return '00000000-0000-0000-0000-000000000001';
  }

  // Validation methods

  hasValidEntries(): boolean {
    return this.allEntries.some(entry => 
      this.hasEntryData(entry) && this.isEntryValid(entry)
    );
  }

  private isEntryValid(entry: VitalSignsModel): boolean {
    // Check if pulse is valid (if present)
    if (entry.pulse !== null && entry.pulse !== undefined) {
      return this.isPulseValid(entry.pulse);
    }
    return true; // Entry is valid if no pulse or pulse is null
  }

  // Helper method to get the second height unit (cm/in) based on the primary height unit
  getSecondHeightUnitText(heightUnitId: string): string {
    // If primary unit is metric (m), second unit is cm
    if (heightUnitId === MT_LengthUnit_m) return this.getUnitDisplayText(MT_LengthUnit_cm);
    // If primary unit is imperial (ft), second unit is in
    if (heightUnitId === IM_LengthUnit_f) return this.getUnitDisplayText(IM_LengthUnit_in);
    // Fallback based on current selection for new records
    return this.selectedUnits?.value === 'metric' ? 'cm' : 'in';
  }

  // Helper method to get unit display text
  getUnitDisplayText(unitId: string): string {
    // First try direct mapping for known constants
    switch (unitId) {
      case MT_WeightUnit_kg: return 'kg';
      case IM_WeightUnit_lb: return 'lb';
      case MT_LengthUnit_m: return 'm';
      case MT_LengthUnit_cm: return 'cm';
      case IM_LengthUnit_f: return 'ft';
      case IM_LengthUnit_in: return 'in';
      case MT_TemperatureUnit_Celsius: return '°C';
      case IM_TemperatureUnit_Fahrenheit: return '°F';
      default:
        // If no direct match, try to parse from constant name pattern
        return this.parseUnitFromConstantName(unitId);
    }
  }

  private parseUnitFromConstantName(unitId: string): string {
    // Find the constant name that matches this GUID
    const constants = {
      [MT_WeightUnit_kg]: 'MT_WeightUnit_kg',
      [IM_WeightUnit_lb]: 'IM_WeightUnit_lb',
      [MT_LengthUnit_m]: 'MT_LengthUnit_m',
      [MT_LengthUnit_cm]: 'MT_LengthUnit_cm',
      [IM_LengthUnit_f]: 'IM_LengthUnit_f',
      [IM_LengthUnit_in]: 'IM_LengthUnit_in',
      [MT_TemperatureUnit_Celsius]: 'MT_TemperatureUnit_Celsius',
      [IM_TemperatureUnit_Fahrenheit]: 'IM_TemperatureUnit_Fahrenheit'
    };

    const constantName = constants[unitId];
    if (constantName) {
      // Extract the part after the second underscore
      const parts = constantName.split('_');
      if (parts.length >= 3) {
        const unit = parts[2];
        // Handle special cases
        if (unit === 'Celsius') return '°C';
        if (unit === 'Fahrenheit') return '°F';
        if (unit === 'f') return 'ft';
        if (unit === 'in') return 'in';
        return unit;
      }
    }
    
    return ''; // Return empty string if no match found
  }

  private createMessage(message: string, type: string): void {
    this.snackBar.open(message, type, {
      duration: 3000,
      verticalPosition: 'top'
    });
  }

  private createVitalSignsNote(savedRecords: VitalSignsDto[]): void {
    if (!savedRecords || savedRecords.length === 0) {
      return;
    }

    // Create a separate note for each vital sign record
    savedRecords.forEach((record, index) => {
      this.createIndividualVitalSignNote(record, index, savedRecords.length);
    });
  }

  private createIndividualVitalSignNote(record: VitalSignsDto, index: number, totalRecords: number): void {
    const vitalDate = this.formatDateTime(record.VitalSignsMeasuredOn);
    let noteString = `Vitals taken on ${vitalDate}\n`;
    
    // Add weight if present and not zero
    if (record.SubjectWeight && record.SubjectWeight !== 0) {
      const weightUnit = this.getUnitDisplayText(record.enumWeightUnitID || '');
      noteString += `Weight = ${record.SubjectWeight} ${weightUnit}\n`;
    }
    
    // Add height if present and not zero
    if (record.SubjectHeight && record.SubjectHeight !== 0) {
      const heightUnit = this.getUnitDisplayText(record.enumLengthUnitID || '');
      let heightText = `Height = ${record.SubjectHeight} ${heightUnit}`;
      
      // Add second height measurement if present
      if (record.SubjectHeight2 && record.SubjectHeight2 !== 0) {
        const secondHeightUnit = this.getSecondHeightUnitText(record.enumLengthUnitID || '');
        heightText += ` ${record.SubjectHeight2} ${secondHeightUnit}`;
      }
      noteString += heightText + '\n';
    }
    
    // Add blood pressure if present and not zero
    if ((record.BloodPressureSystolic && record.BloodPressureSystolic !== 0) || 
        (record.BloodPressureDiastolic && record.BloodPressureDiastolic !== 0)) {
      const systolic = record.BloodPressureSystolic || 0;
      const diastolic = record.BloodPressureDiastolic || 0;
      noteString += `Blood Pressure = ${systolic}/${diastolic} mmHg\n`;
    }
    
    // Add temperature if present and not zero
    if (record.SubjectTemperature && record.SubjectTemperature !== 0) {
      const tempUnit = this.getUnitDisplayText(record.enumTemperatureUnitID || '');
      noteString += `Temperature = ${record.SubjectTemperature} ${tempUnit}\n`;
    }
    
    // Add oxygen saturation if present and not zero
    if (record.OxygenSaturationPCT && record.OxygenSaturationPCT !== 0) {
      noteString += `Oxygen Saturation = ${record.OxygenSaturationPCT}%\n`;
    }
    
    // Add pulse if present and not zero
    if (record.Pulse && record.Pulse !== 0) {
      noteString += `Pulse = ${record.Pulse} bpm\n`;
    }

    // Only create note if we have meaningful content beyond the header
    if (noteString.trim() !== `Vitals taken on ${vitalDate}`) {
      const noteModel = new TdoNoteSaveModel();
      noteModel.associatedObjects = [];
      
      const associatedObject = new TdoNoteAssociatedObject();
      associatedObject.parentObjectName = 'dataPatient';
      associatedObject.objectId = this.globals.currentPatientId;
      noteModel.associatedObjects.push(associatedObject);
      
      noteModel.text = noteString;
      noteModel.patientId = this.globals.currentPatientId;
      noteModel.categoryEnumIds = [NoteCategoryEnum.MedicalHistorySummary];
      noteModel.typeEnumIds = [NoteTypeEnum.Patient];
      
      // Lock the note
      if (!noteModel.dataNote) {
        noteModel.dataNote = new NoteDataModel();
      }
      noteModel.dataNote.isLocked = true;
      
      this.notesService.saveNote(noteModel).subscribe({
        next: (res) => {
          // Only send refresh message after the last note is saved
          if (index === totalRecords - 1) {
            this.messagingService.sendComponentMessage(MessagingConstants.NOTES_REFRESH, '');
          }
        },
        error: (error) => {
          console.error(`Error saving vital signs note ${index + 1}:`, error);
          this.createMessage(`Error saving vital signs note ${index + 1}`, 'Error');
        }
      });
    }
  }
}
